import { LiveData, Service } from '@toeverything/infra';
import { map } from 'rxjs';

import type { DocsService } from '../../doc';
import type { MeetingsStore } from '../stores/meetings';

/**
 * Service for managing Meeting Notes.
 * - Auto-creates Meeting Notes database on init
 * - Provides method to create new meeting docs
 */
export class MeetingsService extends Service {
  private initialized = false;
  private initPromise: Promise<void> | null = null;

  constructor(
    private readonly store: MeetingsStore,
    private readonly docsService: DocsService
  ) {
    super();
    // Auto-initialize on service creation
    this.initPromise = this.initialize();
  }

  /**
   * Initialize - ensure Meeting Notes database exists and has proper columns.
   * Waits for doc list to be synced instead of using a hardcoded delay.
   */
  private async initialize(): Promise<void> {
    if (this.initialized) return;

    // Wait for doc list to be synced (with 10s timeout)
    try {
      await Promise.race([
        this.docsService.list.isReady$.waitFor(ready => ready),
        new Promise(resolve => setTimeout(resolve, 10000)),
      ]);
    } catch {
      // Continue even if waiting fails
    }

    try {
      const existingDocIds = this.store.getMeetingsDocIds();

      // Check if the doc actually exists (not just the property)
      let validDbId: string | null = null;
      for (const docId of existingDocIds) {
        const docRecord = this.docsService.list.doc$(docId).value;
        if (docRecord && !docRecord.trash$.value) {
          validDbId = docId;
          break;
        }
      }

      if (!validDbId) {
        const newDbId = await this.createMeetingNotesDatabase();
        // Verify the database is accessible before considering initialization complete
        // This ensures CRDT sync has completed for the newly created blocks
        const verifyResult = await this.openDatabaseDoc(newDbId, 10);
        if (verifyResult) {
          verifyResult.release();
          console.log('[MeetingsService] Database created and verified accessible');
        } else {
          console.warn('[MeetingsService] Database created but could not verify - may have sync issues');
        }
      } else {
        // Check if existing database has columns, if not add them
        await this.ensureDatabaseColumns(validDbId);

        // Ensure existing database is marked as system doc
        const docRecord = this.docsService.list.doc$(validDbId).value;
        if (docRecord && !docRecord.property$('isSystemDoc').value) {
          docRecord.setProperty('isSystemDoc', true);
        }
      }
      this.initialized = true;
    } catch (error) {
      console.error('[MeetingsService] Failed to initialize:', error);
    }
  }

  /**
   * Open a database doc with priority loading and find the database block.
   * Retries up to maxRetries times if the block tree isn't populated yet.
   */
  private async openDatabaseDoc(
    dbDocId: string,
    maxRetries = 3
  ): Promise<{
    doc: any;
    store: any;
    noteBlock: any;
    databaseBlock: any;
    databaseModel: any;
    datasource: any;
    release: () => void;
  } | null> {
    const { DatabaseBlockDataSource } = await import(
      '@blocksuite/affine/blocks/database'
    );

    const { doc, release } = this.docsService.open(dbDocId);

    try {
      // Use priority loading to speed up sync
      const disposePriority = doc.addPriorityLoad(10);
      await doc.waitForSyncReady();
      disposePriority();

      const store = doc.blockSuiteDoc;

      // Ensure the doc is loaded
      if (!store.ready) {
        store.load();
      }

      for (let attempt = 0; attempt < maxRetries; attempt++) {
        // Log attempt for debugging
        if (attempt > 0) {
          console.log(`[MeetingsService] openDatabaseDoc attempt ${attempt + 1}/${maxRetries}`);
        }

        const noteBlock = store.root?.children.find(
          (child: { flavour: string }) => child.flavour === 'affine:note'
        );

        if (noteBlock) {
          const databaseBlock = noteBlock.children.find(
            (child: { flavour: string }) =>
              child.flavour === 'affine:database'
          );

          if (databaseBlock) {
            const databaseModel = store.getBlock(databaseBlock.id)
              ?.model as any;

            if (databaseModel) {
              const datasource = new DatabaseBlockDataSource(databaseModel);
              return {
                doc,
                store,
                noteBlock,
                databaseBlock,
                databaseModel,
                datasource,
                release,
              };
            }
          }
        }

        // Wait before retrying - use exponential backoff
        if (attempt < maxRetries - 1) {
          const delay = Math.min(500 * Math.pow(2, attempt), 3000);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }

      // All retries exhausted
      release();
      return null;
    } catch (error) {
      release();
      throw error;
    }
  }

  /**
   * Ensure the database has all required columns
   */
  private async ensureDatabaseColumns(dbDocId: string): Promise<void> {
    const { nanoid } = await import('@blocksuite/affine/store');

    const result = await this.openDatabaseDoc(dbDocId);
    if (!result) {
      console.warn(
        '[MeetingsService] Could not find database block for column check'
      );
      return;
    }

    const { databaseModel, datasource, release } = result;

    try {
      // Check existing columns
      const existingColumns = databaseModel.props?.columns$?.value || [];
      const existingNames = existingColumns.map((c: any) => c.name);

      // Add missing columns
      if (!existingNames.includes('Date')) {
        datasource.propertyAdd('end', { type: 'date', name: 'Date' });
      }

      if (!existingNames.includes('Category')) {
        const categoryId = datasource.propertyAdd('end', {
          type: 'select',
          name: 'Category',
        });
        if (categoryId) {
          const categoryOptions = [
            {
              id: nanoid(),
              value: 'Team Sync',
              color: 'var(--affine-tag-blue)',
            },
            {
              id: nanoid(),
              value: 'Planning',
              color: 'var(--affine-tag-green)',
            },
            { id: nanoid(), value: '1:1', color: 'var(--affine-tag-purple)' },
            {
              id: nanoid(),
              value: 'Standup',
              color: 'var(--affine-tag-teal)',
            },
            {
              id: nanoid(),
              value: 'Presentation',
              color: 'var(--affine-tag-yellow)',
            },
            { id: nanoid(), value: 'Retro', color: 'var(--affine-tag-pink)' },
            {
              id: nanoid(),
              value: 'Customer Call',
              color: 'var(--affine-tag-red)',
            },
            {
              id: nanoid(),
              value: 'Review',
              color: 'var(--affine-tag-orange)',
            },
            { id: nanoid(), value: 'Other', color: 'var(--affine-tag-gray)' },
          ];
          datasource.propertyDataSet(categoryId, {
            options: categoryOptions,
          });
        }
      }

      if (!existingNames.includes('Project')) {
        datasource.propertyAdd('end', {
          type: 'rich-text',
          name: 'Project',
        });
      }

      if (!existingNames.includes('Attendees')) {
        datasource.propertyAdd('end', { type: 'member', name: 'Attendees' });
      } else {
        // Check if Attendees column exists but is wrong type (rich-text instead of member)
        const attendeesColumn = existingColumns.find(
          (c: any) => c.name === 'Attendees'
        );
        if (attendeesColumn && attendeesColumn.type !== 'member') {
          datasource.propertyTypeSet(attendeesColumn.id, 'member');
        }
      }
    } catch (error) {
      console.error('[MeetingsService] Error ensuring columns:', error);
    } finally {
      release();
    }
  }

  /**
   * Wait for initialization to complete
   */
  async waitForInit(): Promise<void> {
    if (this.initPromise) {
      await this.initPromise;
    }
  }

  /**
   * LiveData of all meeting doc records (individual meeting notes, not the database)
   */
  meetingsDocs$ = LiveData.from(
    this.store.watchMeetingDocs().pipe(
      map(docIds =>
        docIds
          .map(id => this.docsService.list.doc$(id).value)
          .filter(doc => doc !== undefined && !doc.trash$.value)
      )
    ),
    []
  );

  /**
   * Get the Meeting Notes database ID (only if doc actually exists)
   */
  getMeetingNotesDbId(): string | null {
    const docIds = this.store.getMeetingsDocIds();
    for (const docId of docIds) {
      const docRecord = this.docsService.list.doc$(docId).value;
      if (docRecord && !docRecord.trash$.value) {
        return docId;
      }
    }
    return null;
  }

  /**
   * Check if a doc is the Meeting Notes database
   */
  isMeetingsDoc(docId: string): boolean {
    return this.store.isMeetingsDoc(docId);
  }

  /**
   * Create a new meeting document linked to a database row.
   * The doc shows properties at top (from database) + content below.
   * Throws on failure so the UI can display an error.
   */
  async createNewMeeting(): Promise<string> {
    await this.waitForInit();

    const { DatabaseBlockDataSource } = await import(
      '@blocksuite/affine/blocks/database'
    );
    const { Text } = await import('@blocksuite/affine/store');

    // Get the database doc
    const dbId = this.getMeetingNotesDbId();
    if (!dbId) {
      throw new Error('Meeting Notes database not found');
    }

    // Create a new doc for the meeting
    const docRecord = this.docsService.createDoc();

    // Set up the meeting doc content
    const { doc, release } = this.docsService.open(docRecord.id);
    try {
      const disposePriority = doc.addPriorityLoad(10);
      await doc.waitForSyncReady();
      disposePriority();

      const store = doc.blockSuiteDoc;

      // Set title with today's date
      const today = new Date().toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
      doc.changeDocTitle(`Meeting - ${today}`);

      // Find the note block
      const noteBlock = store.root?.children.find(
        (child: { flavour: string }) => child.flavour === 'affine:note'
      );

      if (noteBlock) {
        // Add Agenda section
        store.addBlock(
          'affine:paragraph',
          { type: 'h2', text: new Text('Agenda') },
          noteBlock.id
        );
        store.addBlock(
          'affine:list',
          { type: 'bulleted', text: new Text('') },
          noteBlock.id
        );

        // Add Notes section
        store.addBlock(
          'affine:paragraph',
          { type: 'h2', text: new Text('Notes') },
          noteBlock.id
        );
        store.addBlock(
          'affine:list',
          { type: 'bulleted', text: new Text('') },
          noteBlock.id
        );

        // Add Action Items section as inline database table
        store.addBlock(
          'affine:paragraph',
          { type: 'h2', text: new Text('Action Items') },
          noteBlock.id
        );

        // Create Action Items database
        const actionItemsDbId = store.addBlock(
          'affine:database',
          {
            columns: [],
            cells: {},
          },
          noteBlock.id
        );

        const actionItemsModel = store.getBlock(actionItemsDbId)?.model;
        if (actionItemsModel) {
          // Set database title
          (actionItemsModel as any).props.title = new Text('Action Items');

          // Create datasource for column management
          const actionItemsDs = new DatabaseBlockDataSource(
            actionItemsModel as any
          );

          // Add table view
          actionItemsDs.viewManager.viewAdd('table');

          // Column 1: Task (Title column is text by default, rename it)
          // The title column already exists, we just need to add the other columns

          // Column 2: Responsible (Member)
          actionItemsDs.propertyAdd('end', {
            type: 'member',
            name: 'Responsible',
          });

          // Column 3: Due Date (Date)
          actionItemsDs.propertyAdd('end', {
            type: 'date',
            name: 'Due Date',
          });

          // Column 4: Completed (Checkbox)
          actionItemsDs.propertyAdd('end', {
            type: 'checkbox',
            name: 'Completed',
          });
        }
      }
    } finally {
      release();
    }

    // Link to the database with retry logic (more retries for production sync)
    const dbResult = await this.openDatabaseDoc(dbId, 5);
    if (!dbResult) {
      // Doc was created with content but couldn't link to database.
      // Still return the doc so the user can use it, but log the issue.
      console.error(
        '[MeetingsService] Could not link meeting to database - properties will not show'
      );
      docRecord.setProperty('isMeetingDoc', true);
      return docRecord.id;
    }

    const { store: dbStore, databaseBlock, databaseModel, datasource, release: releaseDb } = dbResult;

    try {
      // Add a paragraph block as a row with a link to the meeting doc
      const rowId = dbStore.addBlock(
        'affine:paragraph',
        {
          text: new Text([
            {
              insert: ' ',
              attributes: {
                reference: {
                  type: 'LinkedPage',
                  pageId: docRecord.id,
                },
              },
            },
          ]),
        },
        databaseBlock.id
      );

      // Set today's date in the Date column
      try {
        const columns = databaseModel.props?.columns$?.value || [];
        const dateColumn = columns.find((c: any) => c.name === 'Date');
        if (dateColumn && rowId) {
          datasource.cellValueChange(rowId, dateColumn.id, Date.now());
        }
      } catch (columnError) {
        console.warn(
          '[MeetingsService] Could not set date column:',
          columnError
        );
      }

      // Store the database reference on the doc so it can show properties
      docRecord.setProperty('linkedDatabaseDocId', dbId);
      docRecord.setProperty('linkedDatabaseBlockId', databaseBlock.id);
      docRecord.setProperty('linkedDatabaseRowId', rowId);
      docRecord.setProperty('isMeetingDoc', true);
    } finally {
      releaseDb();
    }

    return docRecord.id;
  }

  /**
   * Create the Meeting Notes database with proper columns
   */
  private async createMeetingNotesDatabase(): Promise<string> {
    const { DatabaseBlockDataSource } = await import(
      '@blocksuite/affine/blocks/database'
    );
    const { nanoid, Text } = await import('@blocksuite/affine/store');

    // Create the database doc
    const docRecord = this.docsService.createDoc();

    const { doc, release } = this.docsService.open(docRecord.id);

    try {
      await doc.waitForSyncReady();
      const store = doc.blockSuiteDoc;

      // Set title
      doc.changeDocTitle('Meeting Notes');

      // Find the note block
      const noteBlock = store.root?.children.find(
        (child: { flavour: string }) => child.flavour === 'affine:note'
      );

      if (noteBlock) {
        // Create database block
        const databaseId = store.addBlock(
          'affine:database',
          {
            columns: [],
            cells: {},
          },
          noteBlock.id
        );

        const databaseModel = store.getBlock(databaseId)?.model;

        if (databaseModel) {
          // Set database title
          (databaseModel as any).props.title = new Text('Meeting Notes');

          // Create datasource for property management
          const datasource = new DatabaseBlockDataSource(
            databaseModel as any
          );

          // Add table view
          datasource.viewManager.viewAdd('table');

          // Add Date column
          datasource.propertyAdd('end', {
            type: 'date',
            name: 'Date',
          });

          // Add Category column with preset options
          const categoryId = datasource.propertyAdd('end', {
            type: 'select',
            name: 'Category',
          });

          if (categoryId) {
            const categoryOptions = [
              {
                id: nanoid(),
                value: 'Team Sync',
                color: 'var(--affine-tag-blue)',
              },
              {
                id: nanoid(),
                value: 'Planning',
                color: 'var(--affine-tag-green)',
              },
              {
                id: nanoid(),
                value: '1:1',
                color: 'var(--affine-tag-purple)',
              },
              {
                id: nanoid(),
                value: 'Standup',
                color: 'var(--affine-tag-teal)',
              },
              {
                id: nanoid(),
                value: 'Presentation',
                color: 'var(--affine-tag-yellow)',
              },
              {
                id: nanoid(),
                value: 'Retro',
                color: 'var(--affine-tag-pink)',
              },
              {
                id: nanoid(),
                value: 'Customer Call',
                color: 'var(--affine-tag-red)',
              },
              {
                id: nanoid(),
                value: 'Review',
                color: 'var(--affine-tag-orange)',
              },
              {
                id: nanoid(),
                value: 'Other',
                color: 'var(--affine-tag-gray)',
              },
            ];
            datasource.propertyDataSet(categoryId, {
              options: categoryOptions,
            });
          }

          // Add Project column
          datasource.propertyAdd('end', {
            type: 'rich-text',
            name: 'Project',
          });

          // Add Attendees column (member type for workspace member selection)
          datasource.propertyAdd('end', {
            type: 'member',
            name: 'Attendees',
          });
        }
      }
    } catch (error) {
      console.error('[MeetingsService] Error creating database:', error);
    } finally {
      release();
    }

    // Mark as Meeting Notes database and system doc (hidden from All Docs)
    docRecord.setProperty('isMeetingsDb', true);
    docRecord.setProperty('isSystemDoc', true);

    return docRecord.id;
  }
}
